import React from "react";
import PropTypes from "prop-types";

import { classNames } from "../../../utils/classnames";
import { buttonIcons } from "./buttonIcons";

function ButtonIcon({ icon, size, iconRight }) {
  const IconComponent = buttonIcons[icon];
  const height = size === "sm" ? "h-3/5" : "h-md";
  const classes = iconRight
    ? `${height} ml-xs order-last`
    : `${height} mr-xs order-first`;
  return <IconComponent className={classes} title={icon} />;
}

function Button({
  mode,
  size,
  label,
  onClick,
  icon,
  iconRight,
  fullwidth,
  testID,
  type,
  className,
  disabled,
}) {
  const modeClasses = (modeProp) => {
    let classes = "";
    switch (modeProp) {
      case "dark":
        classes =
          "text-white bg-gray-800 hover:bg-purple ring-purple-300 focus:ring focus:bg-purple";
        break;
      case "dark-outline":
        classes =
          "text-gray-900 bg-transparent border border-gray-900 ring-purple-300 hover:text-purple hover:border-purple focus:ring focus:text-purple focus:border-purple";
        break;
      case "light":
        classes =
          "text-black bg-white ring-offset-transparent ring-pink-300 hover:text-pink focus:ring focus:text-pink";
        break;
      case "light-outline":
        classes =
          "text-white bg-transparent border border-white ring-offset-transparent ring-mint-300 hover:text-mint hover:border-mint focus:ring focus:text-mint focus:border-mint";
        break;
      case "buttonLink":
        classes = "text-black underline";
        break;
      case "vivid":
        classes =
          "text-white bg-purple hover:bg-purple-400 ring-purple-300 focus:ring shadow-[0_4px_0_0_rgba(73,22,169,1),0_8px_0_0_rgba(0,0,0,0.25)]";
        break;
      default:
        break;
    }
    return classes;
  };
  const sizeClasses = (sizeProp) => {
    let classes = "";
    if (sizeProp === "sm") {
      classes = "text-xs h-button-sm px-sm";
    }
    if (sizeProp === "md") {
      classes = "text-sm h-button-md px-sm";
    }
    if (sizeProp === "lg") {
      classes = "text-md h-button-lg px-sm";
    }
    if (sizeProp === "xl") {
      classes = "text-md h-button-xl px-md";
    }
    return classes;
  };

  return (
    <button
      className={classNames(
        "cursor-pointer inline-flex items-center justify-center font-bold rounded transition-colors focus:outline-none",
        `${fullwidth ? "w-full" : ""}`,
        `${modeClasses(mode)}`,
        `${sizeClasses(size)}`,
        `${className}`,
        `${disabled ? "opacity-50 cursor-not-allowed" : ""}`,
      )}
      type={type} //eslint-disable-line
      onClick={onClick}
      data-testid={testID}
      disabled={disabled}
    >
      {label}
      {icon ? (
        <ButtonIcon icon={icon} size={size} iconRight={iconRight} />
      ) : null}
    </button>
  );
}

Button.propTypes = {
  mode: PropTypes.oneOf([
    "dark",
    "dark-outline",
    "light",
    "light-outline",
    "buttonLink",
    "vivid",
  ]),
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  icon: PropTypes.oneOf(Object.keys(buttonIcons)),
  iconRight: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  fullwidth: PropTypes.bool,
  testID: PropTypes.string,
  type: PropTypes.oneOf(["button", "submit"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

Button.defaultProps = {
  mode: "light",
  size: "md",
  icon: undefined,
  iconRight: false,
  fullwidth: false,
  testID: "btn",
  type: "button",
  className: "",
  disabled: false,
  onClick: undefined,
};

ButtonIcon.propTypes = {
  icon: PropTypes.oneOf(Object.keys(buttonIcons)),
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  iconRight: PropTypes.bool,
};

ButtonIcon.defaultProps = {
  icon: undefined,
  size: "md",
  iconRight: false,
};

export default Button;
